"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { readClientForcedVariant } from "@/lib/flags/client-preview-force";

/**
 * Self-optimizing experiment hook.
 *
 * How it works:
 * 1. Reads the feature flag variant key from PostHog
 * 2. Selects matching content from the variants config
 * 3. Fires `$feature_flag_called` (automatic) + `experiment_exposure` on first render
 * 4. Provides `trackConversion()` to fire when the user takes the desired action
 *
 * PostHog handles the rest:
 * - Traffic splitting across variants
 * - Statistical significance calculation
 * - Auto-rollout of the winner (if configured in the experiment)
 *
 * DO NOT CONFIGURE AUTO-ROLLOUT. None is configured today, and none should be.
 * Site-wide conversion counts here are NOT a human denominator: measured
 * 2026-09-06, one scraper fleet was 34% of all traffic and ~4.4k apply hops/day
 * ran at exactly 1:1 hops-to-anon-ids against ~2-4 browser-proved applies. An
 * auto-rollout reading those raw numbers would pick whichever arm the bots
 * happened to land on.
 *
 * Read an experiment through a human filter, both sides:
 *   - PostHog: HUMAN_BROWSER_HOGQL from `@/lib/analytics/trust`
 *   - D1:      TRUSTED_INTERACTION_SQL / the `trust >= 3` hop bar
 * and confirm the arms against D1 rather than PostHog when they disagree —
 * the two apply series have crossed before (2026-09-05).
 *
 * To set up a new experiment:
 * 1. Create a feature flag in PostHog (e.g. "hero-subtitle")
 * 2. Add multivariate variants (e.g. "control", "concise", "bold")
 * 3. Optionally attach JSON payloads to each variant for dynamic content
 * 4. Create an Experiment linked to that flag with a goal metric
 * 5. Call this hook with the flag key and local variant config
 */

export type VariantContent<T> = Record<string, T>;

/**
 * True when `key` is a string that names a configured variant branch. Boolean
 * flags, objects, numbers, and unknown strings are all rejected — they can never
 * select content and must never be reported as an exposure variant.
 */
export function isValidVariantKey<T>(
	key: unknown,
	variants: VariantContent<T>,
): key is string {
	return typeof key === "string" && Object.hasOwn(variants, key);
}

/**
 * Coerce a raw PostHog feature-flag value into a usable variant key, or `null`
 * if the flag hasn't resolved / doesn't map to a configured branch.
 *
 * Guards against two real failures:
 * - `getFeatureFlagResult()`-style objects (the "[object Object]" bug) — any
 *   non-string input returns null instead of being String()-ed into garbage.
 * - A resolved string that isn't one of our branches (stale flag, renamed
 *   variant) — returns null so the caller keeps the "control" default and skips
 *   the exposure rather than logging an unmatchable variant.
 */
export function normalizeVariantKey<T>(
	raw: unknown,
	variants: VariantContent<T>,
): string | null {
	// `false` means the flag is off → control. `undefined`/`null` means not yet
	// loaded → leave caller in its default, unready state.
	if (raw === undefined || raw === null) return null;
	if (raw === false) return "control";
	return isValidVariantKey(raw, variants) ? raw : null;
}

interface UseExperimentOptions<T> {
	/** PostHog feature flag key (must match the experiment's flag) */
	flagKey: string;
	/** Map of variant key -> content. "control" is the default fallback. */
	variants: VariantContent<T>;
	/** Goal event name for conversion tracking (e.g. "job_card_clicked") */
	goalEvent?: string;
	/**
	 * Fire `experiment_exposure` on resolve. Defaults to `true`. Set `false`
	 * for a flag whose live PostHog rollout is pinned to a single variant
	 * (verified via `pnpm ops:flag-roi-report` / the Experiments tab) — an
	 * exposure event that can never see a second arm buys zero statistical
	 * power and permanently fails `pnpm check:experiment-arms`
	 * (T2.2, docs/plans/backlog/data-driven-selfimproving-harness-2026-08-07.md).
	 * Behavior/resolution is untouched by this flag — only the telemetry call
	 * is skipped. See `after-apply-inline.tsx` for the first call site to set
	 * this, and this file's "Dev-time smell check" comment below for the
	 * incident this option resolves.
	 */
	trackExposure?: boolean;
}

interface ExperimentResult<T> {
	/** The resolved content for the active variant */
	content: T;
	/** The active variant key (e.g. "control", "concise") */
	variantKey: string;
	/** Call this when the user completes the goal action */
	trackConversion: (properties?: Record<string, unknown>) => void;
	/** Whether PostHog has loaded and resolved the flag */
	isReady: boolean;
}

/**
 * De-dupe key prefix for the per-user-per-day exposure guard below.
 * localStorage — not a module-scoped Map — so the guard survives a reload, a
 * new tab, or a client-side navigation, all of which reset in-memory module
 * state but not browser storage.
 *
 * History: the previous guard was a `Map<flagKey, pathname>` that only
 * de-duped WITHIN one JS module lifetime. It fixed the original bug (the
 * feed mounting the hook once per job row fired 20+ identical
 * `experiment_exposure` events per pageview — 13,807 events/2wk for one
 * flag), but a reload, a new tab, or the next calendar day re-armed it. That
 * gap let `gate-cta-value-prop` fire ~55x/user before the experiment ended
 * 2026-07-14 (see `src/lib/experiments/gate-cta-value-prop.ts`), which
 * corrupted its exposure denominator right alongside the conversion signal
 * it was supposed to measure. This guard is per (flag, calendar day, browser)
 * instead of per (flag, pathname, JS module instance).
 */
const EXPOSURE_DEDUPE_PREFIX = "ld:exp-exposure:";

/**
 * True if `flagKey`'s exposure already fired today (UTC) on this browser.
 * Fails open (returns false) on any storage error — private-mode Safari,
 * quota exceeded, SSR — so a broken guard degrades to "may fire again"
 * rather than "never fires" (an undercount is worse for an experiment than
 * an occasional duplicate).
 */
export function exposureFiredToday(flagKey: string): boolean {
	if (typeof window === "undefined") return false;
	try {
		const today = new Date().toISOString().slice(0, 10);
		return (
			window.localStorage.getItem(EXPOSURE_DEDUPE_PREFIX + flagKey) === today
		);
	} catch {
		return false;
	}
}

/** Marks `flagKey`'s exposure as fired for today (UTC). Best-effort. */
export function markExposureFiredToday(flagKey: string): void {
	if (typeof window === "undefined") return;
	try {
		const today = new Date().toISOString().slice(0, 10);
		window.localStorage.setItem(EXPOSURE_DEDUPE_PREFIX + flagKey, today);
	} catch {
		// Storage unavailable — best-effort dedupe only, not worth surfacing.
	}
}

export function useExperiment<T>({
	flagKey,
	variants,
	goalEvent,
	trackExposure = true,
}: UseExperimentOptions<T>): ExperimentResult<T> {
	const posthog = usePostHog();
	const exposureTracked = useRef(false);

	// Hold `variants` in a ref so the resolve effect can read the latest config
	// without listing `variants` in its dep array. Callers may pass an inline
	// object literal (new ref every render); depending on it would re-subscribe
	// `onFeatureFlags` on every render and leak listeners.
	const variantsRef = useRef(variants);
	variantsRef.current = variants;

	// Read variant from PostHog — returns null while loading, then the key
	const [variantKey, setVariantKey] = useState<string>("control");
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		// Non-production e2e arm pin (`ld_preview_force` cookie). Fails closed
		// on production: see readClientForcedVariant.
		const forced = readClientForcedVariant(
			flagKey,
			(posthog as { __loaded?: boolean } | undefined)?.__loaded === true,
		);
		if (forced !== null && isValidVariantKey(forced, variantsRef.current)) {
			setVariantKey(forced);
			setIsReady(true);
			return;
		}
		if (!posthog) return;

		function resolve() {
			// `getFeatureFlag()` returns the variant as a primitive (string for
			// multivariate flags, boolean for boolean flags) — NOT the richer
			// `getFeatureFlagResult()` object. Using the result object here and
			// String()-ing it produced the literal "[object Object]" variant that
			// corrupted 1204 exposures across hero/onboarding experiments.
			const raw = posthog.getFeatureFlag(flagKey);
			const key = normalizeVariantKey(raw, variantsRef.current);
			if (key !== null) {
				setVariantKey(key);
				setIsReady(true);
			}
		}

		// Try immediately (flags may already be loaded)
		resolve();

		// Also listen for async load
		posthog.onFeatureFlags(() => {
			resolve();
		});
	}, [posthog, flagKey]);

	// Fire exposure event once per user per day when variant is resolved
	useEffect(() => {
		if (!trackExposure || !isReady || exposureTracked.current || !posthog)
			return;
		// Final integrity guard: never emit an exposure whose variant isn't a
		// real, configured key. A malformed variant silently destroys an
		// experiment's analysis (you can't group by a value that never matches a
		// branch), so we drop the event rather than poison the dataset.
		if (!isValidVariantKey(variantKey, variantsRef.current)) return;
		exposureTracked.current = true;

		// Per-user-per-day de-dupe (see EXPOSURE_DEDUPE_PREFIX above). Also
		// covers the original cross-instance case (multiple components mounting
		// the same flagKey in one pageview): the effects run synchronously in
		// mount order, so the first one to call markExposureFiredToday wins and
		// every later one this same day reads it back as already-fired.
		if (exposureFiredToday(flagKey)) return;
		markExposureFiredToday(flagKey);

		// Dev-time smell check: an exposure event only means something if the
		// flag can actually land a user on more than one branch. A flag that
		// serves a single variant to 100% of traffic (rollout pinned, or a
		// founder-only canary target) can never produce a control comparison —
		// firing `experiment_exposure` for it just burns event volume without
		// buying any statistical power. `after-apply-inline` was the known
		// example (pinned at test=100 since 2026-07-02, see `flag-manifest.ts`)
		// until T2.2 (2026-08-07, data-driven-selfimproving-harness plan) set
		// `trackExposure: false` on its call site — the `if (!trackExposure...)`
		// guard above is the fix. This can't be detected from a single client
		// (no visibility into what other users saw), so it isn't a runtime
		// guard — check live PostHog variant distribution per flag periodically
		// (`pnpm ops:flag-roi-report`, `pnpm check:experiment-arms`, or the
		// Experiments tab) and either fix the rollout split or set
		// `trackExposure: false` for that call site. The same smell applies to
		// any hand-rolled `posthog.capture("experiment_exposure", ...)` call
		// site that doesn't go through this hook — `hunt-redesign` in
		// `HuntChatV2.tsx` was one (single-armed founder-only canary target);
		// its hand-rolled capture was removed the same PR as this fix.

		posthog.capture("experiment_exposure", {
			experiment: flagKey,
			variant: variantKey,
		});
	}, [trackExposure, isReady, variantKey, flagKey, posthog]);

	const content = variants[variantKey] ?? variants.control;

	const trackConversion = (properties?: Record<string, unknown>) => {
		if (!posthog) return;
		const eventName = goalEvent ?? `${flagKey}_conversion`;
		posthog.capture(eventName, {
			experiment: flagKey,
			variant: variantKey,
			...properties,
		});
	};

	return { content, variantKey, trackConversion, isReady };
}
